import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import { postRequest } from '../../../lib/api';
import { useContext, useState } from 'react';
import { AuthContext } from '../../../authContext/context';
import BreadcrumbsWrapper from '../../../components/breadcrumbs';

function generatePassword(length = 12) {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*?';
  const all = upper + lower + digits + special;

  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += digits[Math.floor(Math.random() * digits.length)];
  password += special[Math.floor(Math.random() * special.length)];

  for (let i = 4; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)];
  }

  return password
    .split('')
    .sort(() => 0.5 - Math.random())
    .join('');
}

export default function CreateSolicitor() {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState(generatePassword());

  const regeneratePassword = () => {
    const newPass = generatePassword();
    setTempPassword(newPass);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        email: data.email,
        password: tempPassword,
        forename: data.firstName,
        surname: data.lastName,
        username: data.username,
        phone_number: data.phone_number,
        role: 'SOLICITOR',
        hierarchyLevel: 2, // TIER_2 for solicitors
        status: 'PENDING',
        createdBy: authCtx.user?.userId,
        parentId: authCtx.user?.userId
      };

      const response = await postRequest('private/register', payload, authCtx.token);

      if (response?.status === 201 || response?.success) {
        reset();
        setTempPassword(generatePassword());
        navigate(`/solicitors/view/${response?.data?._id}`);
      }
    } catch (err) {
      console.error('Solicitor creation error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Solicitors', path: '/solicitors' },
          { label: 'Add Solicitor', path: '' },
        ]}
      />
      <br></br>
      <div className="page-header">
        <h1 className="mt-0">Add New Solicitor</h1>
      </div>

      <div className="page-top-formview mb-3">
        <Form onSubmit={handleSubmit(onSubmit)}>
          {/* Personal Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="firstName">
              <Form.Label>First Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="First Name"
                {...register('firstName', { required: 'First name is required' })}
                isInvalid={!!errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="lastName">
              <Form.Label>Last Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Last Name"
                {...register('lastName', { required: 'Last name is required' })}
                isInvalid={!!errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Contact Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Invalid email address',
                  },
                })}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email?.message}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="phone_number">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                placeholder="Phone Number"
                {...register('phone_number')}
              />
            </Form.Group>
          </Row>

          {/* Account Information */}
          <Row className="mb-3">
            <Form.Group as={Col} controlId="username">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                placeholder="Username"
                {...register('username', {
                  required: 'Username is required',
                  minLength: {
                    value: 3,
                    message: 'Minimum 3 characters required',
                  },
                  pattern: {
                    value: /^[a-zA-Z0-9_-]+$/,
                    message: 'Only letters, numbers, underscore and hyphen allowed'
                  }
                })}
                isInvalid={!!errors.username}
              />
              <Form.Control.Feedback type="invalid">
                {errors.username?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/* Temporary Password */}
          <div className="alert alert-info mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>Temporary Password:</strong>{' '}
                <code style={{ backgroundColor: 'rgba(0,0,0,0.1)', padding: '2px 6px', borderRadius: '4px' }}>
                  {tempPassword}
                </code>
                <br />
                <small>The solicitor will be prompted to change this password on first login.</small>
              </div>
              <Button
                size="small"
                onClick={regeneratePassword}
                disabled={loading}
                variant="text"
                style={{ minWidth: 'auto', padding: '4px 8px' }}
              >
                🔄
              </Button>
            </div>
          </div>

          <Stack direction="row" spacing={2}>
            <Button variant="outlined" color="inherit" type="button" onClick={() => navigate('/solicitors')}>
              Cancel
            </Button>
            <Button variant="contained" className='red-bg red-button text-capitalize' type="submit" disabled={loading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
            </Button>
          </Stack>
        </Form>
      </div>
    </>
  );
}
